import { useMemo, useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useStaggerReveal, useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  word: string;
  subtitle: string;
  number: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToWord(key: string): string {
  const words: Record<string, string> = {
    studio: 'Studio',
    one: 'One',
    two: 'Two',
    three: 'Three',
    four: 'Four',
    five: 'Five',
  };
  return words[key] || key;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, true> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    groups[key] = true;
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => ({
    key,
    word: keyToWord(key),
    subtitle: key === 'studio' ? 'Studio' : 'Bedroom',
    number: key === 'studio' ? 'S' : BED_ORDER.indexOf(key).toString(),
    param: key,
  }));
}

// ============================================================================
// Feature slideshow photos from the gallery
//
// The feature image is an auto-advancing slideshow that should showcase
// apartment interiors, so we FILTER OUT "community" photos (pool, gym,
// clubhouse, exterior, leasing office, …) from the Photo Gallery collection and
// LIMIT to the first 4 remaining photos. photo_gallery has no category field,
// so community detection is a keyword heuristic computed here rather than via
// the exact-match `filter` primitive.
// ============================================================================

const COMMUNITY_KEYWORDS = [
  'community', 'exterior', 'building', 'courtyard', 'pool', 'gym', 'fitness',
  'clubhouse', 'club house', 'lounge', 'lobby', 'leasing', 'office', 'playground',
  'park', 'rooftop', 'roof', 'business center', 'dog park', 'garage', 'parking',
  'mail', 'grill', 'bbq', 'spa', 'sauna', 'sundeck', 'amenity', 'amenities',
  'entrance', 'outdoor', 'aerial', 'neighborhood',
];

// Bathrooms, laundry, and closets are also excluded from the feature slideshow.
const BATHROOM_KEYWORDS = [
  'bathroom', 'bath', 'restroom', 'shower', 'toilet', 'powder room', 'vanity',
];

const UTILITY_KEYWORDS = [
  'washer', 'dryer', 'laundry room', 'laundry', 'closet', 'walk-in closet',
  'walk in closet', 'wardrobe',
];

function galleryText(photo: PhotoGallery): string {
  return [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isCommunityPhoto(photo: PhotoGallery): boolean {
  const text = galleryText(photo);
  if (!text) return false;
  return COMMUNITY_KEYWORDS.some((kw) => text.includes(kw));
}

function isExcludedRoomPhoto(photo: PhotoGallery): boolean {
  const text = galleryText(photo);
  if (!text) return false;
  return [...BATHROOM_KEYWORDS, ...UTILITY_KEYWORDS].some((kw) => text.includes(kw));
}

/**
 * Selects the slideshow photos: drop community photos and excluded rooms
 * (bathrooms, laundry, closets), keep only items with a usable image, and limit
 * to the first 4 remaining from the Photo Gallery.
 */
function selectFeaturePhotos(gallery: PhotoGallery[]): PhotoGallery[] {
  return gallery
    .filter(
      (photo) =>
        (photo.image || photo.original_url) &&
        !isCommunityPhoto(photo) &&
        !isExcludedRoomPhoto(photo),
    )
    .slice(0, 4);
}

// ============================================================================
// Feature image slideshow (auto-advancing, mirrors PropertyOverview)
// ============================================================================

function FeatureSlideshow({ images }: { images: PhotoGallery[] }) {
  const [current, setCurrent] = useState(0);
  const count = images.length;
  const safeCurrent = count > 0 ? Math.min(current, count - 1) : 0;

  // Auto-advance every 5s; pauses when there is 0 or 1 slide.
  useEffect(() => {
    if (count <= 1) return;
    const id = window.setInterval(() => {
      setCurrent((p) => (p + 1) % count);
    }, 5000);
    return () => window.clearInterval(id);
  }, [count]);

  if (count === 0) {
    return <div className="fp-feature-image fp-feature-image--empty" />;
  }

  return (
    <div className="fp-feature-image">
      {images.map((img, i) => (
        <div
          key={img.image || img.original_url || i}
          className={`fp-feature-slide${i === safeCurrent ? ' is-active' : ''}`}
          aria-hidden={i === safeCurrent ? undefined : true}
        >
          <VibesitesImage
            url={img.image || img.original_url || ''}
            alt={img.alt_text || img.title || 'Apartment interior'}
            className="w-full h-full object-cover object-center"
            sizes="(max-width: 400px) 100vw, 400px"
            priority={i === 0}
          />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Slot group definitions
// ============================================================================

// "View All Layouts" button (filled)
const viewAllCtaDef: SlotGroupDefinition = {
  label: 'View All Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View All Layouts' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// "Contact Us" button (outlined)
const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Bedroom category card (text-only: number watermark + word + subtitle)
// ============================================================================

function BedCard({ cat }: { cat: BedroomCategory }) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-bed-card group"
    >
      <span className="fp-bed-number" aria-hidden="true">{cat.number}</span>
      <span className="fp-bed-word">{cat.word}</span>
      <span className="fp-bed-divider" aria-hidden="true" />
      <span className="fp-bed-sub">{cat.subtitle}</span>
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // Feature slideshow: filter out community photos and limit to the first 4
  // apartment photos from the Photo Gallery collection.
  const gallery = useCollectionBinding<PhotoGallery>('floorplanslist.gallery', {
    collection: 'photo_gallery',
  });
  const featurePhotos = useMemo(() => selectFeaturePhotos(gallery ?? []), [gallery]);

  const viewAllCta = useSlotGroup('floorplanslist.view_all_cta', viewAllCtaDef);
  const contactCta = useSlotGroup('floorplanslist.contact_cta', contactCtaDef);

  useRevealStyles();
  const imageRef = useScrollReveal<HTMLDivElement>();
  const cardsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;

  return (
    <section className="pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

          {/* ── Left: feature image slideshow ── */}
          <div ref={imageRef} className="reveal-fade-up order-1 md:pl-8 lg:pl-12">
            <FeatureSlideshow images={featurePhotos} />
          </div>

          {/* ── Right: heading + cards + buttons ── */}
          <div className="order-2 text-center md:text-left md:pr-8 lg:pr-12">
            <RichTextContent
              path="floorplanslist.heading"
              label="Section Heading"
              defaultValue="<p>Find Your Perfect Space</p>"
              as="h2"
              className="fp-section-title text-2xl sm:text-3xl md:text-4xl font-bold leading-tight whitespace-nowrap mb-8 md:mb-10"
            />

            {hasCategories ? (
              <>
                <div ref={cardsRef} className="fp-cards-row flex flex-wrap justify-center md:justify-start gap-4 sm:gap-6 mb-10 md:mb-12">
                  {categories.slice(0, 4).map((cat) => (
                    <BedCard key={cat.key} cat={cat} />
                  ))}
                </div>

                <div className="flex flex-wrap md:flex-nowrap justify-center md:justify-start gap-4">
                  <EditableSlotGroup groupPath="floorplanslist.view_all_cta" definition={viewAllCtaDef} className="inline-block md:flex-1 md:min-w-0">
                    <a
                      href={viewAllCta.url || '/floor-plans'}
                      target={viewAllCta._target || '_self'}
                      className="fp-btn fp-btn--filled inline-flex items-center justify-center w-full min-w-[17rem] md:min-w-0 whitespace-nowrap text-sm font-medium tracking-[0.18em] px-8 py-3.5 transition-all duration-300"
                    >
                      {viewAllCta.label || 'View All Layouts'}
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="floorplanslist.contact_cta" definition={contactCtaDef} className="inline-block md:flex-1 md:min-w-0">
                    <a
                      href={contactCta.url || '/contact'}
                      target={contactCta._target || '_self'}
                      className="fp-btn fp-btn--outline inline-flex items-center justify-center w-full min-w-[17rem] md:min-w-0 whitespace-nowrap text-sm font-medium tracking-[0.18em] px-8 py-3.5 transition-all duration-300"
                    >
                      {contactCta.label || 'Contact Us'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </>
            ) : (
              <div className="text-[var(--vibe-color-text-muted)]">
                <p className="font-body text-lg">Floor plans will be displayed here.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* Match the PropertyOverview section title: Josefin Slab display face
           in the body text color. */
        .fp-section-title {
          font-family: "Josefin Slab", Rockwell, Georgia, serif;
          color: var(--vibe-color-text);
        }
        /* "Find Your Perfect Space" must stay on a single line; scale the
           type down on the smallest screens so it never wraps or overflows. */
        @media (max-width: 480px) {
          .fp-section-title {
            font-size: 1.35rem;
          }
        }

        /* Portrait feature slideshow — matches the PropertyOverview slideshow
           sizing (max 400px wide, 5:6 portrait, centered, soft shadow). */
        .fp-feature-image {
          position: relative;
          width: 100%;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          aspect-ratio: 5 / 6;
          overflow: hidden;
          background: var(--vibe-color-surface);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }
        /* Cross-fading slides stacked inside the fixed portrait frame. */
        .fp-feature-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.6s ease;
        }
        .fp-feature-slide.is-active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-feature-slide { transition: none; }
        }

        /* Text-only bedroom category cards: a large faded number watermark
           behind a bold word + small subtitle. */
        .fp-bed-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          text-decoration: none;
          padding-top: 2.5rem;
          /* Larger fixed basis so cards keep their size and wrap onto a second
             line instead of shrinking to fit one row. flex-grow is 0 so they
             pack to the left of the container rather than stretching to fill. */
          flex: 0 1 130px;
          min-width: 130px;
          isolation: isolate;
        }
        .fp-bed-number {
          position: absolute;
          top: -0.45rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: -1;
          font-family: "Josefin Slab", Rockwell, Georgia, serif;
          font-weight: 700;
          font-size: 6.25rem;
          line-height: 1;
          color: color-mix(in srgb, var(--vibe-color-primary) 14%, transparent);
          pointer-events: none;
          transition: color 0.3s ease, transform 0.3s ease;
        }
        .fp-bed-card:hover .fp-bed-number {
          color: color-mix(in srgb, var(--vibe-color-primary) 22%, transparent);
          transform: translateX(-50%) scale(1.06);
        }
        .fp-bed-word {
          font-family: var(--vibe-font-heading);
          font-weight: 800;
          font-size: 1.75rem;
          line-height: 1;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
        }
        .fp-bed-divider {
          display: block;
          width: 2.25rem;
          height: 2px;
          margin: 0.7rem auto;
          background: var(--vibe-color-primary);
          transition: width 0.3s ease;
        }
        .fp-bed-card:hover .fp-bed-divider {
          width: 3.5rem;
        }
        .fp-bed-sub {
          font-family: var(--vibe-font-body);
          font-size: 0.82rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
        }

        /* Buttons — match ParallaxBanner CTA colors (secondary fill / outline).
           Outline uses secondary instead of white so it reads on a light section. */
        .fp-btn {
          border-radius: 0;
        }
        .fp-btn--filled {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          border: 1.5px solid var(--vibe-color-secondary);
        }
        .fp-btn--filled:hover {
          filter: brightness(1.06);
          transform: translateY(-2px);
        }
        .fp-btn--outline {
          background: transparent;
          color: var(--vibe-color-secondary);
          border: 1.5px solid var(--vibe-color-secondary);
        }
        .fp-btn--outline:hover {
          background: color-mix(in srgb, var(--vibe-color-secondary) 12%, transparent);
          transform: translateY(-2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-btn:hover { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your Perfect Space</p>',
    },
  },
  slotGroups: {
    'floorplanslist.view_all_cta': viewAllCtaDef,
    'floorplanslist.contact_cta': contactCtaDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
    // Source for the feature slideshow: community and bathroom photos are
    // filtered out and the list is limited to the first 4 in the component
    // (photo_gallery has no category field, so the exclusion is a keyword
    // heuristic, not a filter).
    'floorplanslist.gallery': {
      label: 'Feature Slideshow Photos',
      collection: 'photo_gallery',
      description: 'Apartment photos shown in the feature slideshow, pulled from the property photo gallery (community, bathroom, laundry, and closet photos excluded, first 4).',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
